import React from 'react';
import { Grid, Box, IconButton, Button, Typography, Link } from '@mui/material';
import { Link as LinkIcon, Description, OpenInNew } from '@mui/icons-material';

const Enlace = ({ data, onClickFinalizar }) => {
    const link = data?.enlace;

    const finalizar = () => {
        onClickFinalizar({
            tipo: data.tipo,
            idDetalleActividad: data.idDetalleActividad,
            idColaborador: data.idColaborador,
            idActividad: data.idActividad,
            idTemario: data.idTemario,
            idSesion: data.idSesion,
            idCurso: data.idCurso
        });
    };

    const detectLinkType = (url) => {
        if (!url) return null;

        // Video patterns
        const videoPatterns = [
            /youtube\.com\/watch/i,
            /youtu\.be\//i,
            /vimeo\.com/i,
            /dailymotion\.com/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];

        // Document patterns
        // Document patterns
        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /docs\.google\.com\/document/i,
            /docs\.google\.com\/presentation/i,
            /drive\.google\.com\/file/i
        ];

        // Image patterns
        const imagePatterns = [/\.jpg$/i, /\.jpeg$/i, /\.png$/i, /\.gif$/i, /\.webp$/i, /\.svg$/i];

        if (videoPatterns.some((pattern) => pattern.test(url))) {
            return 'video';
        }
        if (documentPatterns.some((pattern) => pattern.test(url))) {
            return 'document';
        }
        if (imagePatterns.some((pattern) => pattern.test(url))) {
            return 'image';
        }
        return 'link';
    };

    const getEmbedUrl = (url) => {
        if (!url) return null;

        // YouTube
        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        if (youtubeMatch) {
            return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
        }

        // Vimeo
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }

        return url;
    };

    const generatePreview = (url) => {
        const type = detectLinkType(url);
        // setLinkType(type);

        if (!url || !type) {
            // setPreview(null);
            return;
        }

        switch (type) {
            case 'video':
                const embedUrl = getEmbedUrl(url);
                return (
                    <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                        <iframe
                            title="Vista previa de video"
                            src={embedUrl}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 0,
                                borderRadius: '8px'
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                );
                break;

            case 'document':
                // Para PDFs directos
                if (url.toLowerCase().endsWith('.pdf')) {
                    return (
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento PDF"
                                src={url}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                }
                // Para Google Docs nativos
                else if (url.includes('docs.google.com/document') && url.includes('/edit')) {
                    const embedUrl = url.replace('/edit', '/preview');
                    return (
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento"
                                src={embedUrl}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                }
                // Para PDFs en Google Drive
                else if (url.includes('drive.google.com/file')) {
                    const fileIdMatch = url.match(/\/file\/d\/([^/]+)/);
                    if (fileIdMatch) {
                        const fileId = fileIdMatch[1];
                        const previewUrl = `https://drive.google.com/file/d/${fileId}/preview`;
                        return (
                            <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                                <iframe
                                    title="Vista previa de documento"
                                    src={previewUrl}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 0,
                                        borderRadius: '8px'
                                    }}
                                />
                            </Box>
                        );
                    }
                }
                // Para otros documentos, mostrar como enlace simple
                else {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                            <Description color="primary" />
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <Typography variant="body2" noWrap>
                                    {url}
                                </Typography>
                            </Box>
                            <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                                <OpenInNew fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                }
                break;

            case 'image':
                return (
                    <Box
                        component="img"
                        src={url}
                        alt="Preview"
                        sx={{
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                        }}
                    />
                );
                break;

            case 'link':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                        <LinkIcon color="primary" />
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <Typography variant="body2" noWrap>
                                {url}
                            </Typography>
                        </Box>
                        <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    </Box>
                );
                break;
        }
    };

    if (!link?.enlace) return null;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box
                    sx={{
                        backgroundColor: '#003764', // azul marino
                        borderRadius: '12px',
                        padding: '16px',
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.3)'
                    }}
                >
                    {generatePreview(link?.enlace)}
                </Box>
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="end">
                <Button variant="contained" sx={{ backgroundColor: '#003764', color: '#FFFFFF', borderRadius: '0px' }} onClick={finalizar}>
                    Terminado
                </Button>
            </Grid>
        </Grid>
    );
};

export default Enlace;
